import { FocusScope } from "react-aria";
import { Button, Form, Icon, Modal } from "semantic-ui-react";
import {
  isProbablyEmail,
  isProbablyPhoneNumber,
  isValidEmail,
  isValidPhoneNumber,
} from "../../public/subscription-groups/components/ManageEmailOrPhoneNumberCard";
import { yupResolver } from "@hookform/resolvers";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { ControlledCheckbox, ControlledInput } from "../../../../../../components/ControlledForm";
import classNames from "classnames";
import { useNotifySubscriptionGroups } from "../../../../hooks/useNotifySubscriptionGroups";
import { partition } from "lodash";
import { useNotifyBulkInviteCreate } from "../../../../hooks/useNotifyBulkInviteCreate";

export function AdminAddSubscriberContactMethodModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { subscriptionGroups } = useNotifySubscriptionGroups({});
  const [autoSubscriptionGroups] = partition(
    subscriptionGroups ?? [],
    (subscriptionGroup) => subscriptionGroup.autoSubscribe,
  );
  const { sendBulkInvite, isSendBulkInviteLoading } = useNotifyBulkInviteCreate();

  const { control, handleSubmit, errors, watch } = useForm<{
    emailOrPhoneNumber: string;
    autoEnroll: {
      value: boolean;
      justification: string | null;
    };
  }>({
    resolver: yupResolver(
      yup.object().shape({
        emailOrPhoneNumber: yup
          .string()
          .required("Email Address or Phone Number is required")
          .test("emailOrPhoneNumber", "Invalid Email Address or Phone Number", (value: string) => {
            return isValidPhoneNumber(value) || isValidEmail(value);
          }),
      }),
    ),
    defaultValues: {
      emailOrPhoneNumber: "",
      autoEnroll: {
        value: false,
        justification: null,
      },
    },
  });

  const emailOrPhoneNumber = watch("emailOrPhoneNumber");
  const autoEnroll: boolean = watch("autoEnroll.value");

  const getInputIconAndType = () => {
    if (!emailOrPhoneNumber)
      return {
        icon: null,
        type: null,
      };
    if (isValidEmail(emailOrPhoneNumber))
      return {
        icon: <Icon name='envelope' color='blue' />,
        type: "Email",
      };
    if (isValidPhoneNumber(emailOrPhoneNumber))
      return {
        icon: <Icon name='phone' color='blue' />,
        type: "Phone",
      };
    if (isProbablyEmail(emailOrPhoneNumber))
      return {
        icon: <Icon name='envelope' />,
        type: "Email",
      };
    if (isProbablyPhoneNumber(emailOrPhoneNumber))
      return {
        icon: <Icon name='phone' />,
        type: "Phone",
      };
    return {
      icon: null,
      type: null,
    };
  };

  const { icon, type } = getInputIconAndType();

  const onSubmit = async (data: {
    emailOrPhoneNumber: string;
    autoEnroll: { value: boolean; justification: string | null };
  }) => {
    const subscriptionPreferences: { subscriptionGroupId: number; type: "Subscribe" | "Unsubscribe" }[] = [
      ...autoSubscriptionGroups.map((group) => ({
        subscriptionGroupId: group.id,
        type: "Subscribe" as const,
      })),
    ];

    if (type === "Email") {
      await sendBulkInvite({
        recipients: [
          {
            identifier: data.emailOrPhoneNumber,
            type: "Email",
          },
        ],
        subscriptionPreferences,
        options: {
          resendInvitations: false,
          resendOptedOut: false,
          autoEnroll: data.autoEnroll.value
            ? { value: true, justification: data.autoEnroll.justification ?? "" }
            : null,
        },
      });
    } else if (type === "Phone") {
      await sendBulkInvite({
        recipients: [
          {
            identifier: data.emailOrPhoneNumber,
            type: "Phone",
          },
        ],
        subscriptionPreferences,
        options: {
          resendInvitations: false,
          resendOptedOut: false,
          autoEnroll: data.autoEnroll.value
            ? { value: true, justification: data.autoEnroll.justification ?? "" }
            : null,
        },
      });
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} size='tiny' closeIcon>
      <Modal.Header>Add Contact Method</Modal.Header>
      <FocusScope contain autoFocus restoreFocus>
        <Modal.Content>
          <Form className='tw-space-y-1' onSubmit={handleSubmit(onSubmit)}>
            <Form.Field className='tw-m-0' error={!!errors.emailOrPhoneNumber}>
              <ControlledInput
                control={control}
                errors={errors}
                name='emailOrPhoneNumber'
                icon={icon}
                iconPosition={"right"}
                fluid
                autoComplete='email'
                label='Email Address or Phone Number'
                inputClassName='tw-border-border-dark focus:tw-ring-2 tw-ring-blue'
              />
            </Form.Field>
            <Form.Field className='tw-m-0 tw-flex tw-items-center tw-gap-2'>
              <ControlledCheckbox
                control={control}
                name='autoEnroll.value'
                className='tw-m-0'
                label='Automatically enroll to service without sending an invite'
              />
            </Form.Field>
            <Form.Field
              required
              className={classNames("tw-mt-3", { "tw-hidden": !autoEnroll })}
              error={!!errors.autoEnroll?.justification}
            >
              <ControlledInput
                control={control}
                name='autoEnroll.justification'
                rules={{
                  minLength: {
                    value: 10,
                    message: "Justification must be at least 10 characters.",
                  },
                }}
                required={autoEnroll}
                errors={errors}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button secondary onClick={onClose}>
            Cancel
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            disabled={!!errors.emailOrPhoneNumber || !emailOrPhoneNumber || isSendBulkInviteLoading}
          >
            {autoEnroll ? "Add" : "Send Invite"}
          </Button>
        </Modal.Actions>
      </FocusScope>
    </Modal>
  );
}
